import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserSession, AccountFinal } from '@/api/entities';
import UsageChart from '../components/analytics/UsageChart';
import DailyEngagementChart from '../components/analytics/DailyEngagementChart';
import { PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SignalsTab from '../components/analytics/SignalsTab';
import AccountsTab from '../components/analytics/AccountsTab';
import EngagementTab from '../components/analytics/EngagementTab';
import MostActiveTab from '../components/analytics/MostActiveTab';
import HistoryTab from '../components/analytics/HistoryTab';
import DisengagedTab from '../components/analytics/DisengagedTab';
import CoverageBand from '../components/analytics/CoverageBand';
import { 
  TrendingUp, 
  Target, 
  Users, 
  Zap, 
  Clock, 
  BarChart3, 
  MessageSquare,
  Award,
  Eye,
  FileText,
  Loader2,
  Info,
  AlertCircle,
  CheckCircle2,
  PlayCircle,
  UserCheck,
  UsersRound,
  Linkedin,
  Phone,
  Download
} from 'lucide-react';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';
import { Button } from '@/components/ui/button';
import { downloadAnalyticsTwoPagePdf, formatTenantLabelForPdf } from '@/lib/analyticsPdfExport';
import AnalyticsDateRangeControl from '../components/analytics/AnalyticsDateRangeControl';
import {
  appendAnalyticsWindow,
  formatAnalyticsWindowLabel,
  isValidDateOnly,
  normalizeDateRange,
  previousInclusiveRange,
  windowDayCount,
} from '@/lib/analyticsWindow';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

const MetricCard = ({ icon: Icon, title, value, subtitle, extraSubtitle, loading, tooltip, highlight, compact }) => (
  <Card className={`min-w-0 ${highlight ? 'border-2 border-[#00C2C2] bg-gradient-to-br from-white to-[#00C2C2]/5' : ''}`}>
    <CardContent className={compact ? "p-4 min-w-0" : "p-5 min-w-0"}>
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 min-w-0">
            <p className="text-xs font-medium text-gray-600 break-words min-w-0">{title}</p>
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {loading ? (
            <div className="h-7 w-20 bg-gray-200 rounded animate-pulse"></div>
          ) : (
            <>
              <p className={`text-2xl font-bold text-[#0A1A2F] ${highlight ? 'text-[#00C2C2]' : ''}`}>
                {value}
              </p>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
              {extraSubtitle && <p className="text-xs text-gray-500 mt-0.5">{extraSubtitle}</p>}
            </>
          )}
        </div>
        <div className={`p-2 rounded-full shrink-0 ${highlight ? 'bg-[#00C2C2]' : 'bg-[#00C2C2]/10'}`} aria-hidden>
          <Icon className={`w-5 h-5 ${highlight ? 'text-white' : 'text-[#00C2C2]'}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className="p-1.5 bg-[#00C2C2]/10 rounded">
      <Icon className="w-4 h-4 text-[#00C2C2]" />
    </div>
    <h2 className="text-base font-bold text-[#0A1A2F]">{title}</h2>
  </div>
);

const TierBreakdown = ({ tierData, loading, t, avgAccountFit, avgAccountFitLoading }) => {
  if (loading) {
    return <div className="h-24 bg-gray-100 rounded animate-pulse"></div>;
  }

  const tierColors = {
    T1: 'bg-green-500',
    T2: 'bg-blue-500',
    T3: 'bg-yellow-500',
    T4: 'bg-orange-500',
    unknown: 'bg-gray-400'
  };

  const total = Object.values(tierData || {}).reduce((sum, val) => sum + val, 0);

  const formatAvgFitDisplay = (avg) => {
    const n = Number(avg);
    if (!Number.isFinite(n)) return null;
    const v = n > 0 && n <= 1 ? n * 100 : n;
    const rounded = Math.round(v * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };
  const fitDisplay = formatAvgFitDisplay(avgAccountFit);

  return (
    <Card>
      <CardContent className="p-4">
        {avgAccountFitLoading ? (
          <div className="h-7 w-40 bg-gray-100 rounded animate-pulse mb-3" aria-hidden />
        ) : fitDisplay != null ? (
          <p className="text-base font-bold text-[#0A1A2F] mb-3 tabular-nums">
            {t('avgAccountFitAcrossAccounts')}: {fitDisplay}
          </p>
        ) : (
          <p className="text-sm font-bold text-gray-500 mb-3">
            {t('avgAccountFitAcrossAccounts')}: —
          </p>
        )}
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('accountsByTier')}</h3>
        <div className="space-y-2">
          {Object.entries(tierData || {}).map(([tier, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={tier} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-gray-700">
                    {tier === 'unknown' ? t('unassigned') : tier}
                  </span>
                  <span className="text-gray-600">{count} ({percentage.toFixed(0)}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`${tierColors[tier]} h-1.5 rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const FeaturedMetric = ({ icon: Icon, title, value, unit, color, loading, tooltip }) => (
  <Card className={`bg-gradient-to-br from-${color}-50 to-white border-2 border-${color}-200`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <p className="text-sm font-medium text-gray-700">{title}</p>
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {loading ? (
            <div className="h-10 w-24 bg-gray-200 rounded animate-pulse"></div>
          ) : (
            <>
              <p className={`text-4xl font-bold text-${color}-600`}>{value}</p>
              <p className={`text-xs text-${color}-600 mt-0.5`}>{unit}</p>
            </>
          )}
        </div>
        <div className={`p-3 bg-${color}-100 rounded-full`}>
          <Icon className={`w-7 h-7 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ComingSoonCard = ({ icon: Icon, title, ctaLabel }) => (
  <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-500 mb-1">{title}</p>
          <p className="text-lg font-semibold text-gray-400">{ctaLabel}</p>
        </div>
        <div className="p-2 bg-gray-200 rounded-full opacity-50">
          <Icon className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DIST_PALETTE = [
  '#00C2C2', '#0A7EA4', '#3B82F6', '#8B5CF6', '#EC4899',
  '#F59E0B', '#10B981', '#F97316',
];
const NEUTRAL_COLOR = '#9CA3AF';

const MAX_SLICES = 8;

/** Gray slices: unknown/unmapped buckets + merged "Other (N)" (spec). */
function isNeutralDistributionLabel(label) {
  const s = String(label || '').trim().toLowerCase();
  if (s === '(unknown)' || s === '(unmapped)') return true;
  return /^other\s*\(/i.test(String(label || '').trim());
}

function isNeutralDisengagedLabel(label) {
  const s = String(label || '').trim().toLowerCase();
  if (s.includes('no reason captured') || s === 'reason not captured') return true;
  return /^other\s*\(/i.test(String(label || '').trim());
}

const DISENGAGED_COMPOSITION_LABELS = {
  no_fit: 'Not a fit',
  no_budget: 'No budget',
  not_interested: 'Not Interested',
  wrong_timing: 'Wrong timing',
  competitor_chosen: 'Competitor chosen',
  no_response: 'No response',
  contacted_externally: 'Contacted externally',
  duplicate: 'Duplicate',
  other: 'Other',
  not_added: 'Not added',
  '(no reason captured)': 'Reason not captured',
};

function mapDisengagedCompositionRows(composition) {
  const list = Array.isArray(composition) ? composition : [];
  return list.map((item) => {
    const raw = String(item?.label ?? '').trim();
    const key = raw.toLowerCase();
    const display =
      DISENGAGED_COMPOSITION_LABELS[raw] ||
      DISENGAGED_COMPOSITION_LABELS[key] ||
      raw;
    const n = Number(item?.account_count || 0);
    return {
      label: display,
      account_count: n,
      sends: Number(item?.sends ?? 0),
      persona_count: Number(item?.persona_count ?? 0),
      avg_touches_per_persona: item?.avg_touches_per_persona,
      _rawReason: raw,
    };
  });
}

/**
 * @param {string} [options.valueKey] — slice magnitude field (default `sends`).
 * @param {(label: string) => boolean} [options.isNeutralLabel]
 */
function capSlices(rows, options = {}) {
  const valueKey = options.valueKey || 'sends';
  const isNeutral = options.isNeutralLabel || isNeutralDistributionLabel;
  const sliceValue = (r) => Number(r?.[valueKey] ?? 0);

  const list = Array.isArray(rows) ? rows : [];
  let paletteIdx = 0;
  const withColor = (r) => {
    const neutral = isNeutral(r.label);
    const color = neutral ? NEUTRAL_COLOR : DIST_PALETTE[paletteIdx % DIST_PALETTE.length];
    if (!neutral) paletteIdx += 1;
    const pv = sliceValue(r);
    return { ...r, _pieValue: pv, _color: color };
  };

  if (list.length <= MAX_SLICES) return list.map(withColor);

  const top = list.slice(0, MAX_SLICES - 1).map(withColor);
  const rest = list.slice(MAX_SLICES - 1);
  const otherLabel = `Other (${rest.length})`;
  const otherTooltipLabels = rest.slice(0, 5).map((r) => r.label).join(', ') + (rest.length > 5 ? ', …' : '');

  const otherPrimary = rest.reduce((s, r) => s + sliceValue(r), 0);
  const otherSends = rest.reduce((s, r) => s + Number(r.sends || 0), 0);
  const otherAccounts = rest.reduce((s, r) => s + Number(r.account_count || 0), 0);
  const otherPersonas = rest.reduce((s, r) => s + Number(r.persona_count || 0), 0);

  let wSum = 0;
  let wSends = 0;
  if (valueKey === 'sends') {
    for (const r of rest) {
      const s = Number(r.sends || 0);
      const a = Number(r.avg_touches_per_persona);
      if (Number.isFinite(a) && s > 0) {
        wSum += a * s;
        wSends += s;
      }
    }
  }

  const other = {
    label: otherLabel,
    _otherTooltip: otherTooltipLabels,
    [valueKey]: otherPrimary,
    _pieValue: otherPrimary,
    sends: valueKey === 'sends' ? otherPrimary : otherSends,
    account_count: otherAccounts,
    persona_count: otherPersonas,
    avg_touches_per_persona: valueKey === 'sends' && wSends > 0 ? wSum / wSends : null,
    avg_touches_per_account:
      valueKey === 'sends' && otherAccounts > 0 ? otherPrimary / otherAccounts : null,
    _color: NEUTRAL_COLOR,
  };
  return [...top, other];
}

function weightedAvgTouches(slices) {
  let totalSends = 0;
  let weightedSum = 0;
  for (const s of slices) {
    const sends = Number(s.sends || 0);
    const avg = Number(s.avg_touches_per_persona);
    if (Number.isFinite(avg) && sends > 0) {
      totalSends += sends;
      weightedSum += avg * sends;
    }
  }
  if (totalSends === 0) return null;
  return weightedSum / totalSends;
}

/** Prefer BE field; else sends / account_count in the bucket (touches ≈ sends). */
function avgTouchesPerAccountForSlice(d) {
  if (!d || typeof d !== 'object') return null;
  const direct = Number(d.avg_touches_per_account);
  if (Number.isFinite(direct)) return direct;
  const sends = Number(d.sends || 0);
  const ac = Number(d.account_count || 0);
  if (ac > 0 && sends >= 0) return sends / ac;
  return null;
}

function DistributionPieCard({ title, rows }) {
  const slices = useMemo(() => capSlices(rows, {}), [rows]);
  const totalPersonas = slices.reduce((s, r) => s + Number(r.persona_count || 0), 0);
  const avgTouches = weightedAvgTouches(slices);

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const label = d?._otherTooltip ? `${d.label}: ${d._otherTooltip}` : d?.label;
    const perPersona = Number(d?.avg_touches_per_persona);
    const perAccount = avgTouchesPerAccountForSlice(d);
    return (
      <div className="bg-white border border-gray-200 rounded shadow-md px-3 py-2 text-xs max-w-[220px]">
        <p className="font-semibold text-[#0A1A2F] mb-1">{label}</p>
        <p className="text-gray-600">{Number(d?.sends || 0).toLocaleString()} sends</p>
        <p className="text-gray-500">{Number(d?.account_count || 0)} accounts · {Number(d?.persona_count || 0)} personas</p>
        {Number.isFinite(perPersona) && (
          <p className="text-gray-600 mt-1">Avg touches / persona: {perPersona.toFixed(1)}</p>
        )}
        <p className="text-gray-600">
          Avg touches / account:{' '}
          {perAccount != null && Number.isFinite(perAccount) ? perAccount.toFixed(1) : '—'}
        </p>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-semibold text-[#0A1A2F]">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        <div className="h-[160px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="_pieValue"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={42}
                outerRadius={70}
                paddingAngle={2}
              >
                {slices.map((s, i) => (
                  <Cell key={`cell-${i}`} fill={s._color} />
                ))}
              </Pie>
              <ReTooltip content={customTooltip} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-gray-500 text-center leading-snug mt-1">
          {totalPersonas.toLocaleString()} personas
          {avgTouches !== null && ` · avg ${avgTouches.toFixed(1)} touches/persona`}
        </p>
      </CardContent>
    </Card>
  );
}

function formatQualifiedLeadPenetration(d) {
  const qualified = Number(d?.account_count ?? d?._pieValue ?? 0);
  const total = Number(d?.total_accounts);
  const pct = Number(d?.pct);
  if (Number.isFinite(total) && total > 0) {
    const pctStr = Number.isFinite(pct)
      ? pct.toFixed(1)
      : ((qualified / total) * 100).toFixed(1);
    return `${qualified.toLocaleString()} of ${total.toLocaleString()} (${pctStr}%)`;
  }
  return `${qualified.toLocaleString()} qualified accounts`;
}

function AccountCountPieCard({ title, rows, emptyMessage, showPenetration = false }) {
  const slices = useMemo(() => {
    if (!rows?.length) return [];
    return capSlices(rows, { valueKey: 'account_count', isNeutralLabel: isNeutralDistributionLabel });
  }, [rows]);

  const totalAccounts = slices.reduce((s, r) => s + Number(r._pieValue || 0), 0);
  const hasPieData = slices.some((s) => Number(s._pieValue) > 0);

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const label = d?._otherTooltip ? `${d.label}: ${d._otherTooltip}` : d?.label;
    return (
      <div className="bg-white border border-gray-200 rounded shadow-md px-3 py-2 text-xs max-w-[240px]">
        <p className="font-semibold text-[#0A1A2F] mb-1">{label}</p>
        <p className="text-gray-600">
          {showPenetration ? formatQualifiedLeadPenetration(d) : `${Number(d?._pieValue ?? d?.account_count ?? 0).toLocaleString()} accounts`}
        </p>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-semibold text-[#0A1A2F]">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        <div className="h-[160px]">
          {emptyMessage ? (
            <div className="h-full flex items-center justify-center text-xs text-gray-400 text-center px-4 leading-relaxed">
              {emptyMessage}
            </div>
          ) : hasPieData ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="_pieValue"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={70}
                  paddingAngle={2}
                >
                  {slices.map((s, i) => (
                    <Cell key={`cell-ac-${i}`} fill={s._color} />
                  ))}
                </Pie>
                <ReTooltip content={customTooltip} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-xs text-gray-400">No data</div>
          )}
        </div>
        {!emptyMessage && hasPieData && (
          <p className="text-[11px] text-gray-500 text-center leading-snug mt-1">
            {totalAccounts.toLocaleString()} accounts
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function DisengagedCompositionPieCard({ disengaged }) {
  const rows = useMemo(() => {
    if (!disengaged) return [];
    return mapDisengagedCompositionRows(disengaged.composition);
  }, [disengaged]);

  const slices = useMemo(() => {
    if (!rows.length) return [];
    return capSlices(rows, { valueKey: 'account_count', isNeutralLabel: isNeutralDisengagedLabel });
  }, [rows]);

  const td = Number(disengaged?.total_disengaged ?? 0);
  const pctRaw = Number(disengaged?.pct_disengaged);
  const pctStr = Number.isFinite(pctRaw) ? Number(pctRaw).toFixed(1) : '0.0';

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const label = d?._otherTooltip ? `${d.label}: ${d._otherTooltip}` : d?.label;
    const n = Number(d?._pieValue ?? d?.account_count ?? 0);
    return (
      <div className="bg-white border border-gray-200 rounded shadow-md px-3 py-2 text-xs max-w-[220px]">
        <p className="font-semibold text-[#0A1A2F] mb-1">{label}</p>
        <p className="text-gray-600">{n.toLocaleString()} accounts</p>
      </div>
    );
  };

  if (!disengaged || td === 0) return null;

  const hasPieData = slices.some((s) => Number(s._pieValue) > 0);

  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-semibold text-[#0A1A2F]">Disengaged accounts</CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        <div className="h-[160px]">
          {hasPieData ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="_pieValue"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={70}
                  paddingAngle={2}
                >
                  {slices.map((s, i) => (
                    <Cell key={`cell-dg-${i}`} fill={s._color} />
                  ))}
                </Pie>
                <ReTooltip content={customTooltip} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-xs text-gray-400">No breakdown</div>
          )}
        </div>
        <p className="text-[11px] text-gray-500 text-center leading-snug mt-1">
          <strong>{td.toLocaleString()}</strong> disengaged accounts · <strong>{pctStr}%</strong> of total
        </p>
      </CardContent>
    </Card>
  );
}

function hasMeaningfulTargetProductSlices(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((row) => String(row?.label ?? '').trim().toLowerCase() !== '(unknown)');
}

function formatOverviewAvgTouches(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

function DistributionPiesRow({ distribution }) {
  if (!distribution) return null;

  const showTargetProducts = hasMeaningfulTargetProductSlices(distribution.by_target_products);
  const cards = [
    { title: 'By sector', rows: distribution.by_sector },
    { title: 'By country', rows: distribution.by_country },
    ...(showTargetProducts
      ? [{ title: 'By target products', rows: distribution.by_target_products }]
      : []),
  ].filter((c) => Array.isArray(c.rows) && c.rows.length > 0);

  const showDisengagedHere =
    !showTargetProducts && Number(distribution.disengaged?.total_disengaged || 0) > 0;
  const colCount = cards.length + (showDisengagedHere ? 1 : 0);
  if (colCount === 0) return null;

  const gridClass =
    colCount >= 3 ? 'md:grid-cols-3' : colCount === 2 ? 'md:grid-cols-2' : '';

  return (
    <div className="mt-8">
      <SectionHeader icon={TrendingUp} title="Distribution" />
      <div className={`grid gap-4 grid-cols-1 ${gridClass}`}>
        {cards.map((c) => (
          <DistributionPieCard key={c.title} title={c.title} rows={c.rows} />
        ))}
        {showDisengagedHere && (
          <DisengagedCompositionPieCard disengaged={distribution.disengaged} />
        )}
      </div>
    </div>
  );
}

const LEADS_GENERATED_EMPTY_MESSAGE =
  'No leads yet — mark accounts as qualified to populate this view';

function LeadsGeneratedSection({ distribution }) {
  if (!distribution) return null;

  const qualifiedLeads = distribution.qualified_leads;
  const disengaged = distribution.disengaged;
  const totalQualified = Number(qualifiedLeads?.total_qualified ?? 0);
  const isEmpty = totalQualified === 0;
  const showTargetProducts = hasMeaningfulTargetProductSlices(qualifiedLeads?.by_target_products);
  const showDisengagedHere = showTargetProducts && Number(disengaged?.total_disengaged || 0) > 0;
  const pieCount = 2 + (showTargetProducts ? 1 : 0) + (showDisengagedHere ? 1 : 0);
  const gridClass =
    pieCount >= 4 ? 'lg:grid-cols-4' : pieCount === 3 ? 'lg:grid-cols-3' : 'md:grid-cols-2';

  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-[#00C2C2]/10 rounded">
            <UserCheck className="w-4 h-4 text-[#00C2C2]" />
          </div>
          <h2 className="text-lg font-semibold text-[#0A1A2F]">Leads Generated</h2>
        </div>
        <p className="text-sm text-gray-600">
          Total qualified: <strong className="text-[#0A1A2F]">{totalQualified.toLocaleString()}</strong>
        </p>
      </div>
      <div className={`grid gap-4 grid-cols-1 md:grid-cols-2 ${gridClass}`}>
        <AccountCountPieCard
          title="By sector"
          rows={qualifiedLeads?.by_sector}
          emptyMessage={isEmpty ? LEADS_GENERATED_EMPTY_MESSAGE : null}
          showPenetration
        />
        <AccountCountPieCard
          title="By country"
          rows={qualifiedLeads?.by_country}
          emptyMessage={isEmpty ? LEADS_GENERATED_EMPTY_MESSAGE : null}
          showPenetration
        />
        {showTargetProducts && (
          <AccountCountPieCard
            title="By target products"
            rows={qualifiedLeads?.by_target_products}
            emptyMessage={isEmpty ? LEADS_GENERATED_EMPTY_MESSAGE : null}
            showPenetration
          />
        )}
        {showDisengagedHere && <DisengagedCompositionPieCard disengaged={disengaged} />}
      </div>
    </div>
  );
}

export default function Analytics({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const isAdmin = contextUser?.role === 'admin';
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const [rangeSelect, setRangeSelect] = useState('30');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [analyticsWindow, setAnalyticsWindow] = useState({ mode: 'preset', days: '30' });
  const [sessions, setSessions] = useState([]);
  const [dailyUsageData, setDailyUsageData] = useState([]);
  const [dailyEngagementDays, setDailyEngagementDays] = useState([]);
  const [scopeMode, setScopeMode] = useState('all'); // all | me | user
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [tenantUsers, setTenantUsers] = useState([]);
  const [distribution, setDistribution] = useState(null);
  const [avgAccountFit, setAvgAccountFit] = useState(null);
  const [avgAccountFitLoading, setAvgAccountFitLoading] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState('overview');
  const [activatedTabs, setActivatedTabs] = useState(() => new Set(['overview']));
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  function formatCurrency(value, currency = 'USD') {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `$${n.toLocaleString()}`;
    }
  }

  const effectiveTenant = useMemo(() => {
    return resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');
  }, [currentTenant, contextUser]);

  const analyticsUserId = useMemo(() => {
    if (scopeMode === 'me') return contextUser?.id || contextUser?.user_id || null;
    if (scopeMode === 'user' && selectedUserId !== 'all') return selectedUserId;
    return null;
  }, [scopeMode, selectedUserId, contextUser]);

  const windowLabel = formatAnalyticsWindowLabel(analyticsWindow);

  const handleRangeSelect = (value) => {
    setRangeSelect(value);
    if (value === 'custom') return;
    setTimeRange(value);
    setAnalyticsWindow({ mode: 'preset', days: value });
  };

  const handleApplyCustom = () => {
    const range = normalizeDateRange(draftFrom, draftTo);
    if (!range) return;
    setDraftFrom(range.from);
    setDraftTo(range.to);
    setTimeRange(String(windowDayCount({ mode: 'custom', ...range })));
    setAnalyticsWindow({ mode: 'custom', from: range.from, to: range.to });
  };

  useEffect(() => {
    if (!isAdmin && scopeMode !== 'me') {
      setScopeMode('me');
      setSelectedUserId('all');
    }
  }, [isAdmin, scopeMode]);

  useEffect(() => {
    if (contextUser && effectiveTenant && isAdmin) {
      loadTenantUsers();
    }
  }, [contextUser, effectiveTenant, isAdmin]);

  useEffect(() => {
    let cancelled = false;
    if (!effectiveTenant) {
      setAvgAccountFit(null);
      setAvgAccountFitLoading(false);
      return undefined;
    }
    setAvgAccountFitLoading(true);
    (async () => {
      try {
        const rows = await AccountFinal.filter({ tenant_id: effectiveTenant }, '-account_fit_score', 500);
        if (cancelled) return;
        const scores = (Array.isArray(rows) ? rows : [])
          .filter((a) => a?.is_visible !== false)
          .map((a) => {
            const raw = a?.account_fit_score ?? a?.account_fit ?? a?.fit_score;
            if (raw === null || raw === undefined || raw === '') return null;
            const num = Number(raw);
            return Number.isFinite(num) ? num : null;
          })
          .filter((x) => x != null);
        if (scores.length === 0) setAvgAccountFit(null);
        else setAvgAccountFit(scores.reduce((s, x) => s + x, 0) / scores.length);
      } catch {
        if (!cancelled) setAvgAccountFit(null);
      } finally {
        if (!cancelled) setAvgAccountFitLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveTenant]);

  useEffect(() => {
    if (contextUser) {
      loadMetrics();
    }
  }, [contextUser, effectiveTenant, analyticsWindow, analyticsUserId]);

  useEffect(() => {
    if (!effectiveTenant) return;
    let cancelled = false;
    setDistribution(null);
    const params = new URLSearchParams({ tenant_id: effectiveTenant });
    appendAnalyticsWindow(params, analyticsWindow);
    if (analyticsUserId) params.append('user_id', analyticsUserId);
    // BE: include only accounts/personas with is_visible !== false in distribution buckets.
    params.append('only_visible_accounts', 'true');
    params.append('only_visible_personas', 'true');
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/email-analytics/distribution?${params.toString()}`, { credentials: 'include' });
        if (!cancelled && res.ok) {
          setDistribution(await res.json());
        } else if (!cancelled) {
          setDistribution(null);
        }
      } catch {
        if (!cancelled) setDistribution(null);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveTenant, analyticsWindow, analyticsUserId]);

  const loadTenantUsers = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/user?tenant_id=${effectiveTenant}`,
        {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (!response.ok) return;
      const data = await response.json();
      setTenantUsers(data.users || []);
    } catch (error) {
      console.error('Error loading tenant users:', error);
    }
  };

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const tenantId = effectiveTenant;
      const isCustom = analyticsWindow?.mode === 'custom'
        && isValidDateOnly(analyticsWindow.from)
        && isValidDateOnly(analyticsWindow.to);
      const customRange = isCustom
        ? normalizeDateRange(analyticsWindow.from, analyticsWindow.to)
        : null;
      const rangeDays = customRange
        ? windowDayCount({ mode: 'custom', ...customRange })
        : (parseInt(timeRange, 10) || 30);
      const to = customRange ? `${customRange.to}T23:59:59.999Z` : new Date().toISOString();
      const from = customRange
        ? `${customRange.from}T00:00:00.000Z`
        : new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000).toISOString();
      const prevRange = customRange ? previousInclusiveRange(customRange.from, customRange.to) : null;
      const totalDays = Math.max(1, rangeDays * 2);
      const totalFrom = prevRange
        ? `${prevRange.from}T00:00:00.000Z`
        : new Date(Date.now() - totalDays * 24 * 60 * 60 * 1000).toISOString();

      const toMaybeNumber = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      };

      const applyWindow = (params, { previous = false } = {}) => {
        if (customRange) {
          if (previous && prevRange) {
            params.set('from', prevRange.from);
            params.set('to', customRange.to);
          } else {
            params.set('from', customRange.from);
            params.set('to', customRange.to);
          }
          params.delete('days');
          return params;
        }
        return params;
      };

      // Build all params upfront — no fetch depends on another fetch's response
      const baseParams = applyWindow(new URLSearchParams({ tenant_id: tenantId, from, to }));
      const emailParams = applyWindow(new URLSearchParams({ tenant_id: tenantId, from, to, days: timeRange }));
      const dailyEngParams = applyWindow(new URLSearchParams({ tenant_id: tenantId, days: timeRange }));
      const channelParams = applyWindow(new URLSearchParams({ tenant_id: tenantId, from, to, days: timeRange }));
      const totalBaseParams = applyWindow(
        new URLSearchParams({ tenant_id: tenantId, from: totalFrom, to }),
        { previous: true },
      );
      const totalEmailParams = applyWindow(
        new URLSearchParams({ tenant_id: tenantId, from: totalFrom, to, days: String(totalDays) }),
        { previous: true },
      );
      const totalChannelParams = applyWindow(
        new URLSearchParams({ tenant_id: tenantId, from: totalFrom, to, days: String(totalDays) }),
        { previous: true },
      );
      const crmParams = applyWindow(new URLSearchParams({ tenant_id: tenantId, days: timeRange }));
      if (analyticsUserId) {
        [baseParams, emailParams, dailyEngParams, channelParams,
          totalBaseParams, totalEmailParams, totalChannelParams].forEach(p => p.append('user_id', analyticsUserId));
      }
      const sessionFilter = { tenant_id: tenantId, session_start: { $gte: from } };
      if (analyticsUserId) sessionFilter.user_id = analyticsUserId;

      // Fire all 9 requests in parallel
      const [
        baseResult, emailResult, dailyEngResult, channelResult,
        totalBaseResult, totalEmailResult, totalChannelResult,
        crmResult, loopResult, sessionsResult,
      ] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/api/analytics/metrics?${baseParams}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } }),
        fetch(`${API_BASE_URL}/api/email-analytics/overview?${emailParams}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } }),
        fetch(`${API_BASE_URL}/api/email-analytics/daily-engagement?${dailyEngParams}`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/analytics-v2/channel-response-rates?${channelParams}`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/analytics/metrics?${totalBaseParams}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } }),
        fetch(`${API_BASE_URL}/api/email-analytics/overview?${totalEmailParams}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } }),
        fetch(`${API_BASE_URL}/api/analytics-v2/channel-response-rates?${totalChannelParams}`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/hubspot/pipeline-summary?${crmParams}`, { credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId } }),
        fetch(`${API_BASE_URL}/api/hubspot/loop-summary?days=90`, { credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId } }).then(r => r.ok ? r.json() : null).catch(() => null),
        UserSession.filter(sessionFilter),
      ]);

      // Parse all fetch responses in parallel (bodies must be consumed before processing)
      const safeJson = async (result) => {
        if (result.status !== 'fulfilled') return null;
        const res = result.value;
        if (!res?.ok) return null;
        try { return await res.json(); } catch { return null; }
      };
      const [
        baseData, emailArr, dailyEngJson, channelData,
        totalBaseData, totalEmailArr, totalChannelData, crmData,
      ] = await Promise.all([
        safeJson(baseResult), safeJson(emailResult), safeJson(dailyEngResult), safeJson(channelResult),
        safeJson(totalBaseResult), safeJson(totalEmailResult), safeJson(totalChannelResult), safeJson(crmResult),
      ]);
      // loopResult resolves to parsed JSON already (the fetch chain does .json()), so it does
      // not go through safeJson — passing it there would read a body that is no longer a Response.
      const loopData = loopResult.status === 'fulfilled' ? (loopResult.value || null) : null;
      const fetchedSessions = sessionsResult.status === 'fulfilled' ? (sessionsResult.value || []) : [];

      // --- Assemble data object ---
      const data = { ...(baseData || {}) };

      // Daily usage chart
      const backendDailyUsage = data.daily_activity || data.daily_usage || data.usage_by_day || data.activity_by_day || [];
      setDailyUsageData(Array.isArray(backendDailyUsage) ? backendDailyUsage : []);

      // Email analytics
      const emailData = emailArr?.[0] || {};
      data.reply_rate_percent = emailData.reply_rate || 0;
      data.email_open_rate_percent = emailData.open_rate || 0;
      data.email_click_rate_percent = emailData.click_rate || 0;
      data.email_open_rate_gross_percent = emailData.open_rate_gross || 0;
      data.email_click_rate_gross_percent = emailData.click_rate_gross || 0;
      data.total_emails_sent = emailData.total_sent || 0;
      data.total_replies = emailData.total_replied || 0;
      data.total_opens = emailData.total_opened || 0;
      data.total_clicks = emailData.total_clicked || 0;
      data.scanner_filter_applied = emailData.scanner_filter?.applied || false;
      data.avg_touches_per_persona = emailData.avg_touches_per_persona;
      data.active_personas = emailData.active_personas;
      if (emailArr) console.log('📧 Email analytics loaded:', emailData);

      // Daily engagement
      setDailyEngagementDays(Array.isArray(dailyEngJson?.days) ? dailyEngJson.days : []);

      // Channel rates
      data.linkedin_response_rate = channelData?.linkedin?.response_rate || 0;
      data.linkedin_total_sent = channelData?.linkedin?.total_sent || 0;
      data.call_response_rate = channelData?.call?.response_rate || 0;
      data.call_total_sent = channelData?.call?.total_sent || 0;
      if (channelData) console.log('📱 Channel response rates loaded:', channelData);

      // Previous-period deltas (pure math — no further I/O)
      try {
        const totalEmail = totalEmailArr?.[0] || {};

        const totalNeo = toMaybeNumber(totalBaseData?.neo_recommendations);
        const totalInteractions = toMaybeNumber(totalBaseData?.account_interactions);
        const currentNeo = toMaybeNumber(data.neo_recommendations) ?? 0;
        const currentInteractions = toMaybeNumber(data.account_interactions) ?? 0;
        data.prev_neo_recommendations = totalNeo === null ? null : Math.max(0, totalNeo - currentNeo);
        data.prev_account_interactions = totalInteractions === null ? null : Math.max(0, totalInteractions - currentInteractions);

        const totalSent2 = toMaybeNumber(totalEmail?.total_sent);
        const totalReplies2 = toMaybeNumber(totalEmail?.total_replied);
        const totalOpens2 = toMaybeNumber(totalEmail?.total_opened);
        const totalClicks2 = toMaybeNumber(totalEmail?.total_clicked);
        const currentSent = toMaybeNumber(data.total_emails_sent) ?? 0;
        const currentReplies = toMaybeNumber(data.total_replies) ?? 0;
        const currentOpens = toMaybeNumber(data.total_opens) ?? 0;
        const currentClicks = toMaybeNumber(data.total_clicks) ?? 0;
        const prevSent = totalSent2 === null ? null : Math.max(0, totalSent2 - currentSent);
        const prevReplies = totalReplies2 === null ? null : Math.max(0, totalReplies2 - currentReplies);
        const prevOpens = totalOpens2 === null ? null : Math.max(0, totalOpens2 - currentOpens);
        const prevClicks = totalClicks2 === null ? null : Math.max(0, totalClicks2 - currentClicks);
        data.prev_reply_rate_percent = prevSent == null || prevReplies == null || prevSent <= 0 ? null : Number(((prevReplies / prevSent) * 100).toFixed(2));
        data.prev_email_open_rate_percent = prevSent == null || prevOpens == null || prevSent <= 0 ? null : Number(((prevOpens / prevSent) * 100).toFixed(2));
        data.prev_email_click_rate_percent = prevSent == null || prevClicks == null || prevSent <= 0 ? null : Number(((prevClicks / prevSent) * 100).toFixed(2));

        const totalLinkedinSent2 = toMaybeNumber(totalChannelData?.linkedin?.total_sent);
        const totalLinkedinRate2 = toMaybeNumber(totalChannelData?.linkedin?.response_rate);
        const totalCallSent2 = toMaybeNumber(totalChannelData?.call?.total_sent);
        const totalCallRate2 = toMaybeNumber(totalChannelData?.call?.response_rate);
        const currentLinkedinSent = toMaybeNumber(data.linkedin_total_sent) ?? 0;
        const currentLinkedinRate = toMaybeNumber(data.linkedin_response_rate) ?? 0;
        const currentCallSent = toMaybeNumber(data.call_total_sent) ?? 0;
        const currentCallRate = toMaybeNumber(data.call_response_rate) ?? 0;
        const totalLinkedinResp2 = totalLinkedinSent2 == null || totalLinkedinRate2 == null ? null : (totalLinkedinSent2 * totalLinkedinRate2) / 100;
        const totalCallResp2 = totalCallSent2 == null || totalCallRate2 == null ? null : (totalCallSent2 * totalCallRate2) / 100;
        const prevLinkedinSent = totalLinkedinSent2 == null ? null : Math.max(0, totalLinkedinSent2 - currentLinkedinSent);
        const prevLinkedinResp = totalLinkedinResp2 == null ? null : Math.max(0, totalLinkedinResp2 - (currentLinkedinSent * currentLinkedinRate) / 100);
        const prevCallSent = totalCallSent2 == null ? null : Math.max(0, totalCallSent2 - currentCallSent);
        const prevCallResp = totalCallResp2 == null ? null : Math.max(0, totalCallResp2 - (currentCallSent * currentCallRate) / 100);
        data.prev_linkedin_response_rate = prevLinkedinSent == null || prevLinkedinResp == null || prevLinkedinSent <= 0 ? null : Number(((prevLinkedinResp / prevLinkedinSent) * 100).toFixed(2));
        data.prev_call_response_rate = prevCallSent == null || prevCallResp == null || prevCallSent <= 0 ? null : Number(((prevCallResp / prevCallSent) * 100).toFixed(2));
      } catch (prevError) {
        console.error('Error computing previous-period metrics:', prevError);
        data.prev_neo_recommendations = null;
        data.prev_account_interactions = null;
        data.prev_reply_rate_percent = null;
        data.prev_linkedin_response_rate = null;
        data.prev_call_response_rate = null;
        data.prev_email_open_rate_percent = null;
        data.prev_email_click_rate_percent = null;
      }

      // CRM
      data.crm_connected = crmData?.connected === true;
      if (data.crm_connected) {
        data.crm_open_deals_count = crmData.open_deals_count || 0;
        data.crm_open_deals_value = crmData.open_deals_value || 0;
        data.crm_deals_created_in_window = crmData.deals_created_in_window || 0;
        data.crm_win_rate_percent = crmData.win_rate_percent || 0;
        data.crm_closed_won_count = crmData.closed_won_count_in_window || 0;
        data.crm_closed_lost_count = crmData.closed_lost_count_in_window || 0;
        data.crm_currency = crmData.currency || 'USD';
        // Progression + engagement — the half of the loop that shows the signal moved something.
        data.crm_stages = Array.isArray(crmData.stages) ? crmData.stages : [];
        data.crm_neo_sourced = crmData.neo_sourced || null;
        // -1 means the read FAILED (usually a missing meetings scope). Keep it distinct from 0:
        // "no meetings booked" and "we could not look" are different answers.
        data.crm_meetings_booked = typeof crmData.meetings_booked_in_window === 'number'
          ? crmData.meetings_booked_in_window
          : -1;
      }
      // The loop: signal -> outcome, and what preceded each deal.
      if (loopData?.connected) {
        data.loop_funnel = Array.isArray(loopData.funnel) ? loopData.funnel : [];
        data.loop_provenance = Array.isArray(loopData.provenance) ? loopData.provenance : [];
        data.loop_has_control = loopData.has_control === true;
        data.loop_window_days = loopData.window_days || 90;
      }

      // Sessions / usage
      setSessions(fetchedSessions);
      const totalMinutes = fetchedSessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
      const uniqueUsers = new Set(fetchedSessions.map(s => s.user_id)).size;
      const weekKey = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        if (!Number.isFinite(d.getTime())) return null;
        const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
        t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7));
        return t.toISOString().slice(0, 10);
      };
      const activeWeeks = new Set(
        fetchedSessions.map((s) => weekKey(s.session_start)).filter(Boolean),
      ).size;
      const avgMinutesPerUserPerWeek =
        uniqueUsers > 0 && activeWeeks > 0
          ? Math.round(totalMinutes / (uniqueUsers * activeWeeks))
          : 0;
      if (fetchedSessions.length > 0 || !data.avg_minutes_in_platform_per_user_per_week) {
        data.avg_minutes_in_platform_per_user_per_week = avgMinutesPerUserPerWeek;
      }

      console.log('📊 Analytics metrics loaded:', data);
      setMetrics(data);
    } catch (error) {
      console.error('Error loading metrics:', error);
      setMetrics(null);
      setDailyEngagementDays([]);
    } finally {
      setLoading(false);
    }
  };

  // [2026-10-04] These two hooks MUST stay above the `if (!contextUser)` early return below.
  // They used to sit AFTER it, so while contextUser was null the component ran two fewer hooks
  // than once it loaded. React counts hooks positionally, and a changing count throws
  // "Rendered more hooks than during the previous render", which blanks the page. It survived
  // only because contextUser is normally populated before first paint; a slow load would hit it.
  // Flagged by eslint react-hooks/rules-of-hooks at the old positions (1124 and 1133).
  const pdfScopeSuffix = useMemo(() => {
    if (scopeMode === 'me') return 'My activity';
    if (scopeMode === 'user' && selectedUserId !== 'all') {
      const u = tenantUsers.find((x) => (x.id || x.user_id) === selectedUserId);
      return u?.full_name || u?.email || selectedUserId;
    }
    return undefined;
  }, [scopeMode, selectedUserId, tenantUsers]);

  const handleDownloadPdf = useCallback(async () => {
    if (!effectiveTenant || pdfGenerating) return;
    setPdfError(null);
    setPdfGenerating(true);
    try {
      await downloadAnalyticsTwoPagePdf({
        apiBaseUrl: API_BASE_URL,
        tenantId: effectiveTenant,
        tenantLabel: formatTenantLabelForPdf(effectiveTenant),
        days: analyticsWindow.mode === 'custom' ? undefined : timeRange,
        analyticsWindow,
        userId: analyticsUserId || undefined,
        scopeSuffix: pdfScopeSuffix,
        timeSavedHours: metrics?.total_hours_saved != null ? Number(metrics.total_hours_saved) : null,
        priorPeriodRates: {
          open_rate: metrics?.prev_email_open_rate_percent,
          click_rate: metrics?.prev_email_click_rate_percent,
          reply_rate: metrics?.prev_reply_rate_percent,
        },
      });
    } catch (e) {
      console.error(e);
      setPdfError("Couldn't generate PDF — try again");
    } finally {
      setPdfGenerating(false);
    }
  }, [
    analyticsUserId,
    effectiveTenant,
    metrics?.total_hours_saved,
    metrics?.prev_email_click_rate_percent,
    metrics?.prev_email_open_rate_percent,
    metrics?.prev_reply_rate_percent,
    pdfGenerating,
    pdfScopeSuffix,
    analyticsWindow,
    timeRange,
  ]);

  if (!contextUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  const metricDescriptions = {
    accounts_with_signal_updates: "Accounts that received momentum or persona activity updates",
    tenders_generated: "Live Deals in your primary product categories",
    tenders_reviewed: "Number of times users viewed tender details",
    digest_platform_clicks: "Unique clicks on app.neogtm.com links inside alert-digest emails — the re-engagement signal from the digest.",
    accounts_touched_via_neogtm: "Accounts with at least one message sent/exported or task completed",
    accounts_with_buyer_champion: "Accounts where both Economic Buyer AND Champion personas were engaged",
    multithreaded_accounts_ge3: "Accounts with 3 or more personas engaged",
    playbooks_run: "Number of playbooks executed",
    neo_recommendations: "Total AI-generated content (briefs, outreach, suggestions)",
    account_interactions: "Messages sent/exported + tasks completed",
    reply_rate_percent: "Percentage of delivered emails that received a reply (excludes bounced/invalid emails)",
    linkedin_response_rate: "Percentage of LinkedIn messages that received a confirmed response (manually tracked)",
    call_response_rate: "Percentage of calls that resulted in a confirmed response (manually tracked)",
    email_open_rate_percent: "Likely human opens (scanner traffic filtered). Gross figure shown as footnote.",
    email_click_rate_percent: "Likely human clicks (scanner traffic filtered). Gross figure shown as footnote.",
    total_hours_saved: "Weighted by activity: playbook generated 45min, morning/account brief 30min, message drafted 20min, AI recommendation 20min, account enriched 15min, chatbot answer 10min, account interaction 10min, tender reviewed 10min, persona asset attached 10min, Sales Map edit 10min.",
    avg_minutes_in_platform_per_user_per_week: "Average active time per user per week"
  };

  const formatDeltaText = (current, previous, { asRatePoints = false } = {}) => {
    if (previous === null || previous === undefined) return '';
    const c = Number(current);
    const p = Number(previous);
    if (!Number.isFinite(c) || !Number.isFinite(p)) return '';
    const nominal = c - p;
    const relative = p === 0 ? null : (nominal / Math.abs(p)) * 100;
    const nominalDigits = asRatePoints ? 1 : 0;
    const nominalValue = `${nominal >= 0 ? '+' : ''}${nominal.toFixed(nominalDigits)}${asRatePoints ? ' pp' : ''}`;
    const relativeValue = relative === null ? 'n/a' : `${relative >= 0 ? '+' : ''}${relative.toFixed(1)}%`;
    return `${nominalValue} (${relativeValue}) vs previous period`;
  };


  return (
    <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
      <div className="bg-white border-b mb-6 -mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#0A1A2F] flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-[#00C2C2]" />
                {t('gtmAnalytics')}
              </h1>
              <p className="text-lg text-[#5C6B7A] mt-1">{t('trackYourGoToMarketPerformance')}</p>
              <p className="text-sm text-[#5C6B7A] mt-1">
                {t('viewing')}: {
                  scopeMode === 'all'
                    ? t('allTeamActivity')
                    : scopeMode === 'me'
                      ? t('myActivity')
                      : tenantUsers.find(u => (u.id || u.user_id) === selectedUserId)?.full_name
                        || tenantUsers.find(u => (u.id || u.user_id) === selectedUserId)?.email
                        || t('specificTeammate')
                }
                {' · '}
                {windowLabel}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {isAdmin && (
              <Select value={scopeMode} onValueChange={(v) => { setScopeMode(v); if (v !== 'user') setSelectedUserId('all'); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allTeamActivity')}</SelectItem>
                  <SelectItem value="me">{t('myActivity')}</SelectItem>
                  <SelectItem value="user">{t('specificTeammate')}</SelectItem>
                </SelectContent>
              </Select>
              )}
              {isAdmin && scopeMode === 'user' && (
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder={t('chooseTeammate')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('chooseTeammate')}</SelectItem>
                    {tenantUsers.map((u) => (
                      <SelectItem key={u.id || u.user_id} value={u.id || u.user_id}>
                        {u.full_name || u.email || u.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <AnalyticsDateRangeControl
                selectValue={rangeSelect}
                onSelectValue={handleRangeSelect}
                draftFrom={draftFrom}
                draftTo={draftTo}
                onDraftFrom={setDraftFrom}
                onDraftTo={setDraftTo}
                onApplyCustom={handleApplyCustom}
                t={t}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  disabled={pdfGenerating || !effectiveTenant}
                  onClick={handleDownloadPdf}
                >
                  {pdfGenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                  ) : (
                    <Download className="w-4 h-4 mr-2" aria-hidden />
                  )}
                  {pdfGenerating ? 'Generating…' : 'Download PDF'}
                </Button>
                {pdfError ? (
                  <span className="text-sm text-red-600 max-w-[220px]" role="alert">
                    {pdfError}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-0">
        <Tabs value={analyticsTab} onValueChange={(v) => { setAnalyticsTab(v); setActivatedTabs(prev => { const s = new Set(prev); s.add(v); return s; }); }} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">{t('overview')}</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="most-active">{t('mostActive')}</TabsTrigger>
            <TabsTrigger value="signals">{t('signals')}</TabsTrigger>
            <TabsTrigger value="accounts">{t('accounts')}</TabsTrigger>
            <TabsTrigger value="engagement">{t('engagement')}</TabsTrigger>
            <TabsTrigger value="disengaged">Disengaged</TabsTrigger>
            <TabsTrigger value="history">{t('history')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div id="analytics-overview-pane" className="space-y-8 pb-8">

      {/* 1. TOP-OF-FUNNEL */}
      <div className="mt-6">
        <SectionHeader icon={TrendingUp} title={t('topOfFunnelIntelligence')} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard
            icon={AlertCircle}
            title={t('accountsWithSignals')}
            value={loading ? '...' : metrics?.accounts_with_signal_updates || 0}
            loading={loading}
            tooltip={metricDescriptions.accounts_with_signal_updates}
            compact
          />
          <MetricCard
            icon={FileText}
            title={t('tendersGenerated')}
            value={loading ? '...' : metrics?.tenders_generated || 0}
            loading={loading}
            tooltip={metricDescriptions.tenders_generated}
            compact
          />
          <MetricCard
            icon={Eye}
            title={t('tendersReviewed')}
            value={loading ? '...' : metrics?.tenders_reviewed || 0}
            loading={loading}
            tooltip={metricDescriptions.tenders_reviewed}
            compact
          />
          <MetricCard
            icon={MessageSquare}
            title="Digest clicks back to platform"
            value={loading ? '...' : metrics?.digest_platform_clicks || 0}
            subtitle={
              loading
                ? ''
                : `${metrics?.digest_delivered || 0} sent · ${metrics?.digest_opens || 0} opens`
            }
            loading={loading}
            tooltip="Unique clicks back into the app from alert-digest emails (e.g. Visit NEO GTM, View All Alerts). Counts links pointing to app.neogtm.com only — the re-engagement signal."
            compact
          />
        </div>
      </div>

      {/* 2. COVERAGE & TIER */}
      <div className="mt-8">
        <SectionHeader icon={Target} title={t('coverageTierPenetration')} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Tier breakdown first */}
          <TierBreakdown
            tierData={metrics?.accounts_per_tier}
            loading={loading}
            t={t}
            avgAccountFit={avgAccountFit}
            avgAccountFitLoading={avgAccountFitLoading}
          />
          
          {/* Other metrics */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard
                icon={CheckCircle2}
                title={t('accountsTouched')}
                value={loading ? '...' : metrics?.accounts_touched_via_neogtm || 0}
                subtitle={loading ? '' : `T1: ${metrics?.accounts_touched_by_tier?.T1 || 0}%, T2: ${metrics?.accounts_touched_by_tier?.T2 || 0}%, T3: ${metrics?.accounts_touched_by_tier?.T3 || 0}%, T4: ${metrics?.accounts_touched_by_tier?.T4 || 0}%`}
                loading={loading}
                tooltip={metricDescriptions.accounts_touched_via_neogtm}
                highlight
                compact
              />
              <MetricCard
                icon={UserCheck}
                title={t('buyerChampion')}
                value={loading ? '...' : metrics?.accounts_with_buyer_champion || 0}
                subtitle={loading ? '' : `T1: ${metrics?.buyer_champion_by_tier?.T1 || 0}%, T2: ${metrics?.buyer_champion_by_tier?.T2 || 0}%, T3: ${metrics?.buyer_champion_by_tier?.T3 || 0}%, T4: ${metrics?.buyer_champion_by_tier?.T4 || 0}%`}
                loading={loading}
                tooltip={metricDescriptions.accounts_with_buyer_champion}
                compact
              />
              <MetricCard
                icon={UsersRound}
                title={t('multiThreaded')}
                value={loading ? '...' : metrics?.multithreaded_accounts_ge3 || 0}
                subtitle={loading ? '' : `T1: ${metrics?.multithreaded_by_tier?.T1 || 0}%, T2: ${metrics?.multithreaded_by_tier?.T2 || 0}%, T3: ${metrics?.multithreaded_by_tier?.T3 || 0}%, T4: ${metrics?.multithreaded_by_tier?.T4 || 0}%`}
                loading={loading}
                tooltip={metricDescriptions.multithreaded_accounts_ge3}
                compact
              />
              <MetricCard
                icon={PlayCircle}
                title={t('playbooksRun')}
                value={loading ? '...' : metrics?.playbooks_run || 0}
                subtitle={loading ? '' : `T1: ${metrics?.playbooks_by_tier?.T1 || 0}%, T2: ${metrics?.playbooks_by_tier?.T2 || 0}%, T3: ${metrics?.playbooks_by_tier?.T3 || 0}%, T4: ${metrics?.playbooks_by_tier?.T4 || 0}%`}
                loading={loading}
                tooltip={metricDescriptions.playbooks_run}
                compact
              />
            </div>
          </div>
        </div>
      </div>

      {/* 3. OUTBOUND EXECUTION */}
      <div className="mt-8">
        <SectionHeader icon={Zap} title={t('outboundExecution')} />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <MetricCard
            icon={MessageSquare}
            title={t('neoRecommendations')}
            value={loading ? '...' : metrics?.neo_recommendations || 0}
            subtitle={loading ? '' : formatDeltaText(metrics?.neo_recommendations, metrics?.prev_neo_recommendations)}
            loading={loading}
            tooltip={metricDescriptions.neo_recommendations}
            compact
          />
          <MetricCard
            icon={Users}
            title={t('accountInteractions')}
            value={loading ? '...' : metrics?.account_interactions || 0}
            subtitle={loading ? '' : formatDeltaText(metrics?.account_interactions, metrics?.prev_account_interactions)}
            loading={loading}
            tooltip={metricDescriptions.account_interactions}
            compact
          />
          <MetricCard
            icon={MessageSquare}
            title={t('emailReplyRate')}
            value={loading ? '...' : `${metrics?.reply_rate_percent || 0}%`}
            subtitle={loading ? '' : formatDeltaText(metrics?.reply_rate_percent, metrics?.prev_reply_rate_percent, { asRatePoints: true })}
            loading={loading}
            tooltip={metricDescriptions.reply_rate_percent}
            compact
          />
          <MetricCard
            icon={Linkedin}
            title={t('linkedinResponse')}
            value={loading ? '...' : `${metrics?.linkedin_response_rate || 0}%`}
            subtitle={loading ? '' : `${metrics?.linkedin_total_sent || 0} ${t('sent')}`}
            extraSubtitle={loading ? '' : formatDeltaText(metrics?.linkedin_response_rate, metrics?.prev_linkedin_response_rate, { asRatePoints: true })}
            loading={loading}
            tooltip={metricDescriptions.linkedin_response_rate}
            compact
          />
          <MetricCard
            icon={Phone}
            title={t('callResponse')}
            value={loading ? '...' : `${metrics?.call_response_rate || 0}%`}
            subtitle={loading ? '' : `${metrics?.call_total_sent || 0} ${t('calls')}`}
            extraSubtitle={loading ? '' : formatDeltaText(metrics?.call_response_rate, metrics?.prev_call_response_rate, { asRatePoints: true })}
            loading={loading}
            tooltip={metricDescriptions.call_response_rate}
            compact
          />
          <MetricCard
            icon={Eye}
            title={t('emailOpenRate')}
            value={loading ? '...' : `${metrics?.email_open_rate_percent || 0}%`}
            subtitle={loading ? '' : `${metrics?.email_open_rate_gross_percent || 0}% gross`}
            extraSubtitle={loading ? '' : formatDeltaText(metrics?.email_open_rate_percent, metrics?.prev_email_open_rate_percent, { asRatePoints: true })}
            loading={loading}
            tooltip={metricDescriptions.email_open_rate_percent}
            compact
          />
          <MetricCard
            icon={Target}
            title={t('emailClickRate')}
            value={loading ? '...' : `${metrics?.email_click_rate_percent || 0}%`}
            subtitle={loading ? '' : `${metrics?.email_click_rate_gross_percent || 0}% gross`}
            extraSubtitle={loading ? '' : formatDeltaText(metrics?.email_click_rate_percent, metrics?.prev_email_click_rate_percent, { asRatePoints: true })}
            loading={loading}
            tooltip={metricDescriptions.email_click_rate_percent}
            compact
          />
          <MetricCard
            icon={Users}
            title="Avg touches / persona"
            value={loading ? '...' : formatOverviewAvgTouches(metrics?.avg_touches_per_persona)}
            loading={loading}
            tooltip="Average touches per distinct persona in the window, including playbook steps (LinkedIn, calls, manual completions) beyond email sends."
            compact
          />
        </div>
      </div>

      {/* DISTRIBUTION PIES */}
      <DistributionPiesRow distribution={distribution} />
      <LeadsGeneratedSection distribution={distribution} />

      {/* Daily engagement (replaces large Time Saved chart) + compact time saved; usage below */}
      <div className="mt-8 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-1 flex flex-col min-h-0">
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white flex-1 flex flex-col min-h-[220px]">
              <CardHeader className="pb-2 text-center border-b border-purple-100">
                <CardTitle className="text-lg text-[#0A1A2F]">{t('timeSaved')}</CardTitle>
              </CardHeader>
              <CardContent className="p-5 flex flex-col flex-1 items-center justify-center text-center">
                <div className="p-3.5 bg-purple-100 rounded-full mb-4">
                  <Clock className="w-10 h-10 text-purple-600" aria-hidden />
                </div>
                <p className="text-sm text-gray-600 mb-1">{t('totalHoursSaved')}</p>
                {loading ? (
                  <div className="h-9 w-28 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <p className="text-2xl font-bold text-purple-600 tabular-nums">
                    {metrics?.total_hours_saved != null ? Number(metrics.total_hours_saved).toFixed(1) : '0.0'}
                    <span className="text-sm font-normal text-purple-600/90 ml-1.5">{t('hours')}</span>
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="lg:col-span-3 min-w-0">
            <DailyEngagementChart days={dailyEngagementDays} loading={loading} />
          </div>
        </div>

        <div>
          <SectionHeader icon={Award} title={t('usage')} />
          <Card className="bg-gradient-to-br from-[#00C2C2]/5 to-white border-2 border-[#00C2C2]/20 h-full">
            <CardContent className="p-5 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('dailyActivity')}</p>
                  <p className="text-2xl font-bold text-[#00C2C2] mt-1">
                    {loading ? '...' : metrics?.avg_minutes_in_platform_per_user_per_week || 0}
                    <span className="text-sm font-normal text-gray-600 ml-2">{t('minPerUserWeek')}</span>
                  </p>
                </div>
                <div className="p-3 bg-[#00C2C2]/10 rounded-full">
                  <Award className="w-7 h-7 text-[#00C2C2]" />
                </div>
              </div>
              {loading ? (
                <div className="h-32 bg-gray-100 rounded animate-pulse" />
              ) : (
                <UsageChart sessions={sessions} dailyData={dailyUsageData} timeRange={timeRange} analyticsWindow={analyticsWindow} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

            </div>
          </TabsContent>

          {/* Pipeline lives on its own tab: it is CRM data, it loads from a different system
            * than everything else on Overview, and it had grown long enough to bury the usage
            * charts above it. The data still loads with the main metrics call, so switching to
            * this tab costs no extra request. */}
          <TabsContent value="pipeline">
            <div id="analytics-pipeline-pane">
            <CoverageBand />
        {/* 6. PIPELINE — live CRM data when connected, placeholder otherwise */}
        <div>
          <SectionHeader icon={Target} title={t('pipelineDeals')} />
          {metrics?.crm_connected ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <MetricCard
                icon={FileText}
                title={t('openPipeline')}
                value={loading ? '...' : (metrics?.crm_open_deals_count || 0).toLocaleString()}
                subtitle={loading ? '' : formatCurrency(metrics?.crm_open_deals_value, metrics?.crm_currency)}
                loading={loading}
                tooltip="Open deals (not closed-won, not closed-lost) currently in the pipeline."
                compact
              />
              <MetricCard
                icon={Users}
                title={t('dealsCreated')}
                value={loading ? '...' : (metrics?.crm_deals_created_in_window || 0).toLocaleString()}
                subtitle={loading ? '' : windowLabel}
                loading={loading}
                tooltip="Deals created in the selected time window."
                compact
              />
              <MetricCard
                icon={Award}
                title={t('winRate')}
                value={loading ? '...' : `${metrics?.crm_win_rate_percent || 0}%`}
                subtitle={
                  loading ? '' :
                  `${metrics?.crm_closed_won_count || 0} won / ${metrics?.crm_closed_lost_count || 0} lost`
                }
                loading={loading}
                tooltip="Closed-won as % of (closed-won + closed-lost) within the window."
                compact
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ComingSoonCard icon={Users} title={t('meetingsBooked')} ctaLabel={t('connectCrm')} />
              <ComingSoonCard icon={FileText} title={t('dealsCreated')} ctaLabel={t('connectCrm')} />
              <ComingSoonCard icon={Award} title={t('winRate')} ctaLabel={t('connectCrm')} />
            </div>
          )}

          {/* PIPELINE PROGRESSION + ENGAGEMENT — closing the loop.
            * The three cards above say how much is open. They cannot show a deal MOVING, and
            * movement is the only evidence that a signal did anything. This strip reads the CRM
            * back: where the pipeline actually sits by stage, meetings booked off the back of it,
            * and how much of it NEO sourced. */}
          {metrics?.crm_connected && (metrics?.crm_stages?.length > 0 || metrics?.crm_neo_sourced) && (
            <div className="mt-5">
              <div className="flex items-baseline justify-between mb-3">
                <h4 className="text-base font-semibold text-[#0A1A2F]">Pipeline progression</h4>
                <span className="text-sm text-gray-400">last {metrics?.crm_window_days || 30} days</span>
              </div>

              {/* 🔴 CONSTRAIN THE STRIP. Letting the bar flex to the full card width pushed the
                * value column hard against the container's right edge — measured right:1736 against
                * a wrapper ending at 1736 — so it read as cut off even though nothing overflowed
                * (scrollWidth === clientWidth). Capping the whole block shrinks the bars and brings
                * the numbers back into the eye's path instead of stranding them at the margin. */}
              <div className="space-y-2">
                {(metrics?.crm_stages || []).map((st) => {
                  const max = Math.max(1, ...(metrics?.crm_stages || []).map((x) => x.value || 0));
                  const pct = Math.round(((st.value || 0) / max) * 100);
                  const closed = /won|lost/i.test(st.label || '');
                  return (
                    <div key={st.id} className="flex items-center gap-3">
                      <div className="w-52 shrink-0 text-sm text-gray-600 truncate" title={st.label}>{st.label}</div>
                      <div className="flex-1 min-w-0 h-7 bg-gray-50 rounded overflow-hidden">
                        {/* The count rides INSIDE the bar rather than in its own column — one less
                          * column to cross with the eye, and the number sits where the quantity is.
                          * minWidth keeps a one-deal bar wide enough to hold its own digit; a
                          * zero-count stage gets no bar and no number, and the value column
                          * already carries an em dash for it. */}
                        {/* Render NOTHING for an empty stage. width:0 was not enough — px-1.5
                          * still gave the element 12px, so a zero-count stage painted a stub that
                          * read as a deal that isn't there. */}
                        {st.count > 0 && (
                          <div
                            className={`h-full rounded flex items-center justify-end px-1.5 ${closed ? 'bg-[#00C2C2]' : 'bg-[#0A1A2F]'}`}
                            style={{ width: `${pct}%`, minWidth: '26px' }}
                          >
                            {/* Navy on the teal bar: white on #00C2C2 is ~2:1 and unreadable. */}
                            <span className={`text-xs font-semibold tabular-nums leading-none ${closed ? 'text-[#0A1A2F]' : 'text-white'}`}>
                              {st.count}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="w-28 shrink-0 text-sm text-[#0A1A2F] text-right tabular-nums whitespace-nowrap">
                        {st.value ? formatCurrency(st.value, metrics?.crm_currency) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <MetricCard
                  icon={Users}
                  title={t('meetingsBooked')}
                  value={loading ? '...' : (metrics?.crm_meetings_booked >= 0 ? metrics.crm_meetings_booked.toLocaleString() : '—')}
                  subtitle={metrics?.crm_meetings_booked >= 0 ? windowLabel : 'CRM did not return meetings'}
                  loading={loading}
                  tooltip="Meetings created in your CRM within the window. A dash means the meeting data could not be read — not that none were booked."
                  compact
                />
                <MetricCard
                  icon={Target}
                  title="NEO-influenced pipeline"
                  value={loading ? '...' : formatCurrency(metrics?.crm_neo_sourced?.open_value || 0, metrics?.crm_currency)}
                  subtitle={loading ? '' : `${metrics?.crm_neo_sourced?.open_count || 0} open deal(s)`}
                  loading={loading}
                  tooltip="Open deals carrying NEO's stamp — pipeline where a NEO signal was in play."
                  compact
                />
                <MetricCard
                  icon={Award}
                  title="NEO-influenced closed won"
                  value={loading ? '...' : formatCurrency(metrics?.crm_neo_sourced?.won_value_in_window || 0, metrics?.crm_currency)}
                  subtitle={loading ? '' : windowLabel}
                  loading={loading}
                  tooltip="Closed-won value within the window on deals NEO influenced."
                  compact
                />
              </div>

              {/* ── THE LOOP: signal -> outcome, and what preceded each deal ──────────────
                * The cards above say what is in the CRM. This says whether a NEO signal led to
                * any of it. Each step is counted from the system that OWNS that fact — signals and
                * drafts from NEO, everything from meetings rightwards from the CRM — because
                * counting a CRM outcome from NEO's own send log would be marking our own homework.
                * Steps that came back -1 could not be READ (a scope, usually) and are omitted
                * rather than rendered as zero. */}
              {(metrics?.loop_funnel?.length > 0) && (
                <div className="mt-6">
                  <h4 className="text-base font-semibold text-[#0A1A2F] mb-4">Signal to outcome</h4>
                  <div className="flex items-stretch gap-1.5 overflow-x-auto pb-1">
                    {metrics.loop_funnel.filter((f) => f.value >= 0).map((f, idx, arr) => (
                      <div key={f.key} className="flex items-stretch flex-1">
                        <div className={`rounded px-3 py-2 min-w-[118px] flex-1 px-4 py-3 ${f.source === 'neo' ? 'bg-[#0A1A2F]' : 'bg-[#E8FAFA] border border-[#B8EDED]'}`}>
                          <div className={`text-2xl font-semibold tabular-nums leading-tight ${f.source === 'neo' ? 'text-white' : 'text-[#0A1A2F]'}`}>
                            {f.value.toLocaleString()}
                          </div>
                          <div className={`text-xs leading-snug mt-1 ${f.source === 'neo' ? 'text-gray-300' : 'text-gray-500'}`}>
                            {f.label}
                          </div>
                        </div>
                        {idx < arr.length - 1 && (
                          <div className="flex items-center px-1 text-gray-300 text-base">›</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-baseline justify-between gap-4 mt-2">
                    <p className="text-xs text-gray-400">
                      Dark steps are Neo&apos;s; light steps are read back from your CRM.
                    </p>
                    <p className="text-xs text-gray-400 whitespace-nowrap">Last {metrics?.loop_window_days || 90} days</p>
                  </div>
                </div>
              )}

              {(metrics?.loop_provenance?.length > 0) && (
                <div className="mt-6">
                  <h4 className="text-base font-semibold text-[#0A1A2F] mb-3">What each deal started from</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-100">
                          <th className="text-left font-normal py-2 pr-4">Account</th>
                          <th className="text-left font-normal py-1.5 pr-3">Signal that preceded it</th>
                          <th className="text-center font-normal py-2 pr-4 whitespace-nowrap">Days to deal</th>
                          <th className="text-left font-normal py-1.5 pr-3">Stage</th>
                          <th className="text-right font-normal py-2">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.loop_provenance.map((r, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-2.5 pr-4 text-[#0A1A2F]">{r.account || '—'}</td>
                            <td className="py-2.5 pr-4 text-gray-600">
                              {r.signal_type
                                ? <>{r.signal_type}{r.signal_person ? ` · ${r.signal_person}` : ''}<span className="text-gray-400"> · {r.signal_date}</span></>
                                : <span className="text-gray-400">no signal on record</span>}
                            </td>
                            <td className="py-2.5 pr-4 text-center tabular-nums text-gray-600">
                              {r.days_signal_to_deal == null ? '—' : r.days_signal_to_deal}
                            </td>
                            <td className="py-2.5 pr-4">
                              <span className={r.is_won ? 'text-[#00A8A8] font-medium' : 'text-gray-600'}>{r.stage}</span>
                            </td>
                            <td className="py-2.5 text-right tabular-nums text-[#0A1A2F]">
                              {formatCurrency(r.amount, metrics?.crm_currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
            </div>
          </TabsContent>

          <TabsContent value="most-active">
            <div id="analytics-most-active-pane">
              {activatedTabs.has('most-active') && <MostActiveTab tenantId={effectiveTenant} timeRange={timeRange} analyticsWindow={analyticsWindow} userId={analyticsUserId} />}
            </div>
          </TabsContent>

          <TabsContent value="signals">
            {activatedTabs.has('signals') && <SignalsTab tenantId={effectiveTenant} timeRange={timeRange} analyticsWindow={analyticsWindow} userId={analyticsUserId} />}
          </TabsContent>

          <TabsContent value="accounts">
            {activatedTabs.has('accounts') && <AccountsTab tenantId={effectiveTenant} timeRange={timeRange} analyticsWindow={analyticsWindow} userId={analyticsUserId} />}
          </TabsContent>

          <TabsContent value="engagement">
            {activatedTabs.has('engagement') && <EngagementTab tenantId={effectiveTenant} timeRange={timeRange} analyticsWindow={analyticsWindow} userId={analyticsUserId} />}
          </TabsContent>

          <TabsContent value="disengaged">
            {activatedTabs.has('disengaged') && <DisengagedTab tenantId={effectiveTenant} />}
          </TabsContent>

          <TabsContent value="history">
            {activatedTabs.has('history') && <HistoryTab tenantId={effectiveTenant} timeRange={timeRange} analyticsWindow={analyticsWindow} currentUser={contextUser} userId={analyticsUserId} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}